import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  type AccountWithBuckets,
  CategoryKind,
  RecurringDirection,
  TransactionType,
  type AppCurrency,
  type ReconciliationWriteContext,
  type RecurringExpense,
  type Transaction,
} from '@financial/shared';
import { useAccounts } from '../accounts/useAccounts';
import { useCategories } from '../categories/useCategories';
import { useRecurring } from '../recurring/useRecurring';
import { useCreateTransaction, useTransactions, useUpdateTransaction } from './useTransactions';
import { formatMoney, toDateInputValue } from '../../lib/format';
import { getApiErrorMessage } from '../../lib/errors';
import { Field } from '../../components/ui/Field';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  accountAvailableBalance,
  transactionBalanceChange,
  type AccountBalanceChange,
} from '../../lib/balancePreview';

/**
 * Enlaza un movimiento con el gasto/ingreso fijo al que corresponde.
 *
 * Enlazarlo lo saca del gasto variable —ya se cuenta como compromiso fijo del
 * mes— y marca ese mes como generado en el recurrente, para que el cron no cree
 * un duplicado.
 */
function RecurringLinkSelect({
  id,
  type,
  currency,
  value,
  onChange,
}: {
  id: string;
  type: TransactionType;
  currency?: AppCurrency;
  value: string;
  onChange: (value: string) => void;
}) {
  const recurringQ = useRecurring();
  const wanted =
    type === TransactionType.INCOME ? RecurringDirection.INCOME : RecurringDirection.EXPENSE;
  const options = (recurringQ.data ?? []).filter(
    (r: RecurringExpense) => r.direction === wanted && (!currency || r.currency === currency),
  );
  if (options.length === 0) return null;

  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">Corresponde a</span>
      <select
        id={id}
        className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">No es un movimiento fijo</option>
        {options.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      <span className="mt-1.5 block text-xs leading-4 text-slate-500">
        Si es la renta, una suscripción o tu nómina, enlázalo: deja de contar como gasto variable
        porque ya está descontado como compromiso fijo del mes.
      </span>
    </label>
  );
}

function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es-MX')
    .trim();
}

function isValidDateInput(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

type NoteSuggestion = Pick<Transaction, 'note' | 'accountId' | 'categoryId'>;

function NoteAutocomplete({
  value,
  suggestions,
  accountName,
  categoryName,
  onChange,
  onPick,
}: {
  value: string;
  suggestions: NoteSuggestion[];
  accountName: Map<string, string>;
  categoryName: Map<string, string>;
  onChange: (value: string) => void;
  onPick: (suggestion: NoteSuggestion) => void;
}) {
  const [focused, setFocused] = useState(false);
  const visible = focused && value.trim().length > 0 && suggestions.length > 0;

  return (
    <div className="relative">
      <label htmlFor="qtx-note" className="mb-1.5 block text-sm font-semibold text-slate-700">
        ¿Qué fue?
      </label>
      <div className="relative">
        <span
          className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400"
          aria-hidden
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 13.5V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7.5" />
            <path d="m14 17 2.5 2.5L21 15" />
          </svg>
        </span>
        <input
          id="qtx-note"
          value={value}
          autoComplete="off"
          autoFocus
          data-modal-autofocus="true"
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Ej. Cinépolis, súper, nómina…"
          className="tap-target h-[52px] w-full rounded-xl border border-slate-200 bg-white py-2 pl-11 pr-3 text-base font-medium text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-500 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          aria-autocomplete="list"
          aria-expanded={visible}
          aria-controls="qtx-note-suggestions"
        />
      </div>
      <p className="mt-1.5 text-xs leading-4 text-slate-500">
        Escribe el comercio o motivo. Puedes reutilizar una nota de tu historial.
      </p>
      {visible && (
        <ul
          id="qtx-note-suggestions"
          role="listbox"
          className="absolute z-20 mt-2 max-h-52 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-[0_18px_36px_-16px_rgba(15,23,42,.35)]"
        >
          <li className="px-2.5 py-1.5 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
            Coincidencias en tu historial
          </li>
          {suggestions.map((suggestion) => (
            <li key={`${suggestion.note}-${suggestion.accountId}-${suggestion.categoryId ?? ''}`}>
              <button
                type="button"
                role="option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onPick(suggestion);
                  setFocused(false);
                }}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition hover:bg-slate-50"
              >
                <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
                  {suggestion.note}
                </span>
                <span className="shrink-0 text-xs text-slate-600">
                  {categoryName.get(suggestion.categoryId ?? '') ??
                    accountName.get(suggestion.accountId) ??
                    'Usar'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BalanceChangeRow({
  label,
  before,
  after,
  currency,
}: {
  label: string;
  before: number;
  after: number;
  currency: AppCurrency;
}) {
  const beforeLabel = formatMoney(before, currency);
  const afterLabel = formatMoney(after, currency);
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-600">{label}</dt>
      <dd className="shrink-0 font-semibold tabular-nums text-slate-900">
        <span className="sr-only">
          Pasa de {beforeLabel} a {afterLabel}
        </span>
        <span aria-hidden className="flex items-center gap-1.5">
          <span>{beforeLabel}</span>
          <span className="text-slate-500">→</span>
          <span>{afterLabel}</span>
        </span>
      </dd>
    </div>
  );
}

function TransactionBalancePreview({
  change,
  type,
}: {
  change: AccountBalanceChange;
  type: TransactionType;
}) {
  const { account } = change;
  const hasBuckets = account.buckets.length > 0;
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5" aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Revisa antes de guardar</h3>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            Este {type === TransactionType.INCOME ? 'ingreso se sumará al' : 'gasto se restará del'}{' '}
            dinero disponible de {account.name}.
          </p>
        </div>
        <span className="shrink-0 rounded-md bg-white px-2 py-1 text-[11px] font-bold text-slate-600 shadow-sm">
          {account.currency}
        </span>
      </div>
      <dl className="mt-3 space-y-2 border-t border-slate-200 pt-3 text-sm">
        <BalanceChangeRow
          label={hasBuckets ? 'Saldo total' : 'Saldo de la cuenta'}
          before={change.total.before}
          after={change.total.after}
          currency={account.currency}
        />
        {hasBuckets && (
          <BalanceChangeRow
            label="Disponible para usar"
            before={change.available.before}
            after={change.available.after}
            currency={account.currency}
          />
        )}
      </dl>
    </section>
  );
}

function recurringOccurrenceLabel(transaction: Transaction): string {
  const occurrenceMonth = transaction.recurringOccurrenceMonth ?? transaction.date.slice(0, 7);
  const [year, month] = occurrenceMonth.split('-').map(Number);
  if (!year || !month) return 'este mes';
  const label = new Intl.DateTimeFormat('es-MX', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function RecurringAccountChangePreview({
  transaction,
  previousAccount,
  nextAccount,
}: {
  transaction: Transaction;
  previousAccount: AccountWithBuckets;
  nextAccount: AccountWithBuckets;
}) {
  const movementDelta =
    transaction.type === TransactionType.INCOME ? transaction.amount : -transaction.amount;
  const reverseDelta = -movementDelta;
  const previousBucket = transaction.bucketId
    ? previousAccount.buckets.find((bucket) => bucket.id === transaction.bucketId)
    : undefined;
  const previousAvailable = accountAvailableBalance(previousAccount);
  const nextAvailable = accountAvailableBalance(nextAccount);
  const previousAvailableAfter = previousBucket
    ? previousAvailable
    : previousAvailable + reverseDelta;

  return (
    <section className="rounded-2xl border border-blue-200 bg-blue-50/70 p-3.5" aria-live="polite">
      <h3 className="text-sm font-bold text-slate-900">Revisa el cambio de cuenta</h3>
      <p className="mt-1 text-xs leading-5 text-slate-700">
        Sólo se corregirá el registro de {recurringOccurrenceLabel(transaction)}. El monto, la fecha
        y la cuenta programada para los próximos movimientos no cambiarán.
      </p>
      <div className="mt-3 grid gap-3 border-t border-blue-100 pt-3 text-sm">
        <div className="rounded-xl bg-white/80 p-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-[.06em] text-slate-500">
            Cuenta anterior · {previousAccount.name}
          </p>
          <dl className="space-y-2">
            <BalanceChangeRow
              label="Saldo total"
              before={previousAccount.balance}
              after={previousAccount.balance + reverseDelta}
              currency={previousAccount.currency}
            />
            {previousAccount.buckets.length > 0 && (
              <BalanceChangeRow
                label="Disponible para usar"
                before={previousAvailable}
                after={previousAvailableAfter}
                currency={previousAccount.currency}
              />
            )}
            {previousBucket && (
              <BalanceChangeRow
                label={`Apartado ${previousBucket.name}`}
                before={previousBucket.balance}
                after={previousBucket.balance + reverseDelta}
                currency={previousAccount.currency}
              />
            )}
          </dl>
        </div>
        <div className="rounded-xl bg-white/80 p-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-[.06em] text-slate-500">
            Cuenta correcta · {nextAccount.name}
          </p>
          <dl className="space-y-2">
            <BalanceChangeRow
              label="Saldo total"
              before={nextAccount.balance}
              after={nextAccount.balance + movementDelta}
              currency={nextAccount.currency}
            />
            {nextAccount.buckets.length > 0 && (
              <BalanceChangeRow
                label="Disponible para usar"
                before={nextAvailable}
                after={nextAvailable + movementDelta}
                currency={nextAccount.currency}
              />
            )}
          </dl>
        </div>
      </div>
    </section>
  );
}

export interface QuickAddTransactionInitialValues {
  type?: TransactionType;
  amount?: number | string;
  accountId?: string;
  date?: string;
  note?: string;
  /** Protege el movimiento que explica una conciliación contra reintentos y saldos obsoletos. */
  reconciliation?: ReconciliationWriteContext;
}

/**
 * Formulario de alta rápida de movimiento (ingreso/egreso).
 * Se reutiliza en la pantalla de Movimientos, el botón flotante global y la
 * conciliación cuando la diferencia sí corresponde a un hecho faltante.
 */
export function QuickAddTransactionForm({
  onDone,
  initialValues,
  lockAccountingFields = false,
  onBusyChange,
}: {
  onDone: () => void;
  initialValues?: QuickAddTransactionInitialValues;
  /**
   * Conserva tipo, monto, cuenta y fecha cuando el formulario explica una
   * diferencia calculada por otro flujo, como la conciliación de saldo.
   */
  lockAccountingFields?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const historyQ = useTransactions({ limit: 500 });
  const create = useCreateTransaction();

  const [type, setType] = useState<TransactionType>(initialValues?.type ?? TransactionType.EXPENSE);
  const [amount, setAmount] = useState(
    initialValues?.amount === undefined ? '' : String(initialValues.amount),
  );
  const [accountId, setAccountId] = useState(initialValues?.accountId ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [date, setDate] = useState(initialValues?.date ?? toDateInputValue());
  const [note, setNote] = useState(initialValues?.note ?? '');
  const [recurringExpenseId, setRecurringExpenseId] = useState('');

  useEffect(() => {
    onBusyChange?.(create.isPending);
    return () => onBusyChange?.(false);
  }, [create.isPending, onBusyChange]);

  const accounts = accountsQ.data ?? [];
  const categories = (categoriesQ.data ?? []).filter((c) =>
    type === TransactionType.INCOME
      ? c.kind === CategoryKind.INCOME
      : c.kind === CategoryKind.EXPENSE,
  );
  const numericAmount = Number(amount);
  const amountIsValid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0;
  const dateIsValid = isValidDateInput(date);
  const selectedAccount = accounts.find((account) => account.id === accountId);
  const selectedAvailable = selectedAccount ? accountAvailableBalance(selectedAccount) : 0;
  const amountTooLarge =
    type === TransactionType.EXPENSE &&
    amountIsValid &&
    Boolean(selectedAccount) &&
    numericAmount > selectedAvailable + 0.005;
  const balanceChange =
    selectedAccount && amountIsValid && !amountTooLarge
      ? transactionBalanceChange(selectedAccount, type, numericAmount)
      : null;
  const accountName = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );
  const categoryName = useMemo(
    () =>
      new Map(
        (categoriesQ.data ?? []).map((category) => [
          category.id,
          `${category.icon ?? ''} ${category.name}`.trim(),
        ]),
      ),
    [categoriesQ.data],
  );
  const noteSuggestions = useMemo(() => {
    const query = normalizeSearch(note);
    if (!query) return [];
    const found = new Set<string>();
    return (historyQ.data ?? [])
      .filter(
        (transaction) =>
          transaction.type === type &&
          transaction.note &&
          normalizeSearch(transaction.note).includes(query),
      )
      .filter((transaction) => {
        const key = normalizeSearch(transaction.note ?? '');
        if (found.has(key)) return false;
        found.add(key);
        return true;
      })
      .slice(0, 5)
      .map((transaction) => ({
        note: transaction.note,
        accountId: transaction.accountId,
        categoryId: transaction.categoryId,
      }));
  }, [historyQ.data, note, type]);

  const selectType = (nextType: TransactionType) => {
    if (lockAccountingFields) return;
    setType(nextType);
    setCategoryId('');
    setRecurringExpenseId('');
  };

  const applyNoteSuggestion = (suggestion: NoteSuggestion) => {
    setNote(suggestion.note ?? '');
    if (!lockAccountingFields) setAccountId(suggestion.accountId);
    setCategoryId(suggestion.categoryId ?? '');
    setRecurringExpenseId('');
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || !amountIsValid || amountTooLarge || !dateIsValid) return;
    create.mutate(
      {
        accountId: selectedAccount.id,
        type,
        amount: numericAmount,
        date: new Date(date).toISOString(),
        categoryId: categoryId || undefined,
        note: note || undefined,
        recurringExpenseId: lockAccountingFields ? null : recurringExpenseId || null,
        reconciliation: initialValues?.reconciliation,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {lockAccountingFields && (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm leading-5 text-slate-700">
          Tipo, monto, cuenta y fecha quedan fijos para que este movimiento explique exactamente la
          diferencia conciliada. Todavía puedes agregar una categoría y una nota. Al guardarlo
          quedará como evidencia; si después detectas un error, registra una corrección nueva.
        </p>
      )}
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
        <button
          type="button"
          disabled={lockAccountingFields}
          onClick={() => selectType(TransactionType.EXPENSE)}
          data-form-value-change={type !== TransactionType.EXPENSE ? 'true' : undefined}
          aria-pressed={type === TransactionType.EXPENSE}
          className={`tap-target rounded-[9px] py-2 text-sm font-semibold transition disabled:cursor-not-allowed ${
            type === TransactionType.EXPENSE
              ? 'bg-expense text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          Gasto
        </button>
        <button
          type="button"
          disabled={lockAccountingFields}
          onClick={() => selectType(TransactionType.INCOME)}
          data-form-value-change={type !== TransactionType.INCOME ? 'true' : undefined}
          aria-pressed={type === TransactionType.INCOME}
          className={`tap-target rounded-[9px] py-2 text-sm font-semibold transition disabled:cursor-not-allowed ${
            type === TransactionType.INCOME
              ? 'bg-income text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          Ingreso
        </button>
      </div>

      <NoteAutocomplete
        value={note}
        suggestions={noteSuggestions}
        accountName={accountName}
        categoryName={categoryName}
        onChange={setNote}
        onPick={applyNoteSuggestion}
      />

      <label htmlFor="qtx-amount" className="block">
        <span className="mb-1.5 block text-sm font-semibold text-slate-700">Monto</span>
        <span className="relative block">
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-xl font-semibold text-slate-500">
            $
          </span>
          <input
            id="qtx-amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            required
            readOnly={lockAccountingFields}
            aria-readonly={lockAccountingFields || undefined}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="tap-target h-[54px] w-full appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xl font-semibold tracking-tight text-slate-900 outline-none transition read-only:cursor-not-allowed read-only:bg-slate-50 read-only:text-slate-600 placeholder:font-normal placeholder:text-slate-500 focus:border-slate-400 focus:ring-4 focus:ring-slate-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-700">Cuenta</span>
          <select
            className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            value={accountId}
            disabled={lockAccountingFields}
            onChange={(e) => {
              setAccountId(e.target.value);
              setRecurringExpenseId('');
            }}
            required
          >
            <option value="" disabled>
              Elige una cuenta
            </option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {formatMoney(accountAvailableBalance(a), a.currency)} disponibles ·{' '}
                {a.currency}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-700">Categoría</span>
          <select
            className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-600"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ` : ''}
                {c.name}
              </option>
            ))}
          </select>
          <span className="mt-1.5 block text-xs leading-4 text-slate-500">
            Mejora el presupuesto y las alertas.
          </span>
        </label>
      </div>

      {!lockAccountingFields && (
        <RecurringLinkSelect
          id="qtx-recurring"
          type={type}
          currency={selectedAccount?.currency}
          value={recurringExpenseId}
          onChange={setRecurringExpenseId}
        />
      )}

      <DatePicker
        id="qtx-date"
        label="Fecha"
        required
        value={date}
        onChange={setDate}
        disabled={lockAccountingFields}
        helperText="La fecha define dónde aparece en tu historial; el saldo actual se actualiza al guardar."
      />
      {!dateIsValid && (
        <p className="text-sm text-expense" role="alert">
          Elige una fecha válida.
        </p>
      )}
      {balanceChange && <TransactionBalancePreview change={balanceChange} type={type} />}
      {amountTooLarge && selectedAccount && (
        <p
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-5 text-expense"
          role="alert"
        >
          Sólo tienes {formatMoney(selectedAvailable, selectedAccount.currency)} disponibles en{' '}
          {selectedAccount.name}. Reduce el monto o elige otra cuenta.
        </p>
      )}
      {accounts.length === 0 && (
        <p className="text-sm text-warning">Primero crea una cuenta en la sección Cuentas.</p>
      )}
      {create.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={
            create.isPending ||
            accounts.length === 0 ||
            !selectedAccount ||
            !amountIsValid ||
            amountTooLarge ||
            !dateIsValid
          }
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] transition hover:bg-slate-800 active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {create.isPending ? 'Guardando…' : 'Guardar movimiento'}
        </button>
      </div>
    </form>
  );
}

/** Edición puntual que respeta movimientos conciliados por otro flujo. */
export function EditTransactionForm({
  transaction,
  onDone,
}: {
  transaction: Transaction;
  onDone: () => void;
}) {
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const update = useUpdateTransaction();
  const [type, setType] = useState(transaction.type);
  const [amount, setAmount] = useState(String(transaction.amount));
  const [accountId, setAccountId] = useState(transaction.accountId);
  const [categoryId, setCategoryId] = useState(transaction.categoryId ?? '');
  const [date, setDate] = useState(toDateInputValue(transaction.date));
  const [note, setNote] = useState(transaction.note ?? '');
  const [recurringExpenseId, setRecurringExpenseId] = useState(
    transaction.recurringExpenseId ?? '',
  );
  const generatedByRecurring = Boolean(transaction.recurringExpenseId);
  const generatedByLoanPayment = Boolean(transaction.loanPaymentId);
  const accountingIsLocked = generatedByRecurring || generatedByLoanPayment;
  // Los registros creados en 0.12.0 todavía no guardaban el mes durable.
  // También deben poder corregirse; la fecha conserva el período visible.
  const canChangeRecurringAccount = generatedByRecurring && !generatedByLoanPayment;
  const numericAmount = Number(amount);
  const amountIsValid =
    accountingIsLocked ||
    (amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0);
  const dateIsValid = accountingIsLocked || isValidDateInput(date);

  const accounts = accountsQ.data ?? [];
  const compatibleAccounts = accounts.filter(
    (account) => account.currency === transaction.currency,
  );
  const previousAccount = accounts.find((account) => account.id === transaction.accountId);
  const selectedAccount = compatibleAccounts.find((account) => account.id === accountId);
  const accountChanged = canChangeRecurringAccount && accountId !== transaction.accountId;
  const previousBucket = transaction.bucketId
    ? previousAccount?.buckets.find((bucket) => bucket.id === transaction.bucketId)
    : undefined;
  const accountChangeHasInsufficientFunds = Boolean(
    accountChanged &&
    previousAccount &&
    selectedAccount &&
    (transaction.type === TransactionType.EXPENSE
      ? transaction.amount > accountAvailableBalance(selectedAccount) + 0.005
      : transaction.amount >
        (previousBucket?.balance ?? accountAvailableBalance(previousAccount)) + 0.005),
  );
  const nextCategoryId = categoryId || null;
  const nextNote = note || null;
  const categoryChanged = nextCategoryId !== (transaction.categoryId ?? null);
  const noteChanged = nextNote !== (transaction.note ?? null);
  const hasLockedChanges = accountChanged || categoryChanged || noteChanged;
  const categories = (categoriesQ.data ?? []).filter((category) =>
    type === TransactionType.INCOME
      ? category.kind === CategoryKind.INCOME
      : category.kind === CategoryKind.EXPENSE,
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (
      !accountId ||
      !amountIsValid ||
      !dateIsValid ||
      accountChangeHasInsufficientFunds ||
      (accountingIsLocked && !hasLockedChanges)
    )
      return;
    update.mutate(
      {
        id: transaction.id,
        successMessage: accountChanged ? 'Cuenta corregida sólo para este movimiento' : undefined,
        body: accountingIsLocked
          ? {
              // Una ocurrencia fija permite corregir la cuenta de este registro
              // junto con sus datos descriptivos, sin tocar la programación.
              ...(accountChanged ? { accountId } : {}),
              ...(categoryChanged ? { categoryId: nextCategoryId } : {}),
              ...(noteChanged ? { note: nextNote } : {}),
            }
          : {
              accountId,
              type,
              amount: numericAmount,
              date: new Date(date).toISOString(),
              categoryId: categoryId || null,
              // Un apartado sólo es válido dentro de su cuenta original.
              bucketId: accountId === transaction.accountId ? (transaction.bucketId ?? null) : null,
              note: note || null,
              recurringExpenseId: recurringExpenseId || null,
            },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {transaction.recurringExpenseId && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
          Este registro de {recurringOccurrenceLabel(transaction)} ya fue creado por un movimiento
          fijo. Aquí puedes corregir{' '}
          {canChangeRecurringAccount
            ? 'la cuenta usada sólo en esta ocasión, la categoría o la nota'
            : 'la categoría o la nota'}
          . La programación de los próximos movimientos no cambia. Para corregir el monto usa{' '}
          <strong>Cambiar monto de este mes</strong> y para los siguientes usa{' '}
          <strong>Editar próximos movimientos</strong> en Movimientos fijos.
        </p>
      )}
      {transaction.loanPaymentId && (
        <p className="rounded-xl bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">
          Este movimiento pertenece a un pago o cobro de Deudas. Aquí puedes corregir la categoría o
          la nota. El saldo y el calendario sólo se revierten desde el aviso{' '}
          <strong>Deshacer</strong> que aparece justo después de marcarlo.
        </p>
      )}
      {!accountingIsLocked && (
        <>
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => {
                setType(TransactionType.EXPENSE);
                setCategoryId('');
              }}
              data-form-value-change={type !== TransactionType.EXPENSE ? 'true' : undefined}
              aria-pressed={type === TransactionType.EXPENSE}
              className={`tap-target rounded-[9px] py-2 text-sm font-semibold transition ${type === TransactionType.EXPENSE ? 'bg-expense text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Gasto
            </button>
            <button
              type="button"
              onClick={() => {
                setType(TransactionType.INCOME);
                setCategoryId('');
              }}
              data-form-value-change={type !== TransactionType.INCOME ? 'true' : undefined}
              aria-pressed={type === TransactionType.INCOME}
              className={`tap-target rounded-[9px] py-2 text-sm font-semibold transition ${type === TransactionType.INCOME ? 'bg-income text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Ingreso
            </button>
          </div>
          <label htmlFor="etx-amount" className="block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">Monto</span>
            <span className="relative block">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-xl font-semibold text-slate-500">
                $
              </span>
              <input
                id="etx-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                required
                autoFocus
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="tap-target h-[54px] w-full appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xl font-semibold tracking-tight text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">Cuenta</span>
            <select
              className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              required
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {canChangeRecurringAccount && (
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-700">
            Cuenta usada en {recurringOccurrenceLabel(transaction)}
          </span>
          <select
            data-modal-autofocus="true"
            className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
            required
          >
            {compatibleAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} · {formatMoney(accountAvailableBalance(account), account.currency)}{' '}
                disponibles
              </option>
            ))}
          </select>
          <span className="mt-1.5 block text-xs leading-5 text-slate-500">
            Sólo aparecen cuentas en {transaction.currency}. Este cambio corrige los saldos de esta
            ocurrencia; no cambia la cuenta guardada en Movimientos fijos.
          </span>
        </label>
      )}
      {accountChanged && previousAccount && selectedAccount && (
        <RecurringAccountChangePreview
          transaction={transaction}
          previousAccount={previousAccount}
          nextAccount={selectedAccount}
        />
      )}
      {accountChangeHasInsufficientFunds && previousAccount && selectedAccount && (
        <p
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-5 text-expense"
          role="alert"
        >
          {transaction.type === TransactionType.EXPENSE
            ? `Sólo hay ${formatMoney(accountAvailableBalance(selectedAccount), selectedAccount.currency)} disponibles en ${selectedAccount.name}. Elige otra cuenta para mover este gasto.`
            : `Ya no hay saldo suficiente en ${previousAccount.name} para retirar este ingreso. Revisa sus movimientos antes de cambiar la cuenta.`}
        </p>
      )}
      <label className="block">
        <span className="mb-1.5 block text-sm font-semibold text-slate-700">Categoría</span>
        <select
          className="tap-target h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Sin categoría</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.icon ? `${category.icon} ` : ''}
              {category.name}
            </option>
          ))}
        </select>
      </label>
      {!accountingIsLocked && (
        <>
          <RecurringLinkSelect
            id="etx-recurring"
            type={type}
            value={recurringExpenseId}
            onChange={setRecurringExpenseId}
          />
          <DatePicker
            id="etx-date"
            label="Fecha"
            required
            value={date}
            onChange={setDate}
            helperText="La fecha determina en qué período aparecerá el movimiento."
          />
          {!dateIsValid && (
            <p className="text-sm text-expense" role="alert">
              Elige una fecha válida.
            </p>
          )}
        </>
      )}
      <Field
        id="etx-note"
        label="Nota (opcional)"
        helperText="Puedes usarla para dejar una referencia útil al revisar tu historial."
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      {update.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(update.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={
            update.isPending ||
            !accountId ||
            !amountIsValid ||
            !dateIsValid ||
            accountChangeHasInsufficientFunds ||
            (accountingIsLocked && !hasLockedChanges)
          }
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] transition hover:bg-slate-800 active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {update.isPending
            ? 'Guardando…'
            : accountChanged
              ? 'Confirmar cambio de cuenta'
              : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}
